/**
 * usePanelStore â€” Zustand store for UI panel, modal, and analytics state.
 *
 * Extracted from Workbench.tsx as part of Phase 0 full.
 * Owns: floating card visibility, modals, analytics workspace, compliance,
 *       granular action statuses, findings, debug mode.
 */

import { create } from 'zustand';
import type { ScanTypology } from '../types/graph';
import type { ActionStatuses } from '../components/NodeInspector/GranularActionsRow';
import type { Finding } from '../components/NodeInspector/FindingsStrip';

export interface ComplianceHit {
    address: string;
    sanctioned: boolean;
    hit?: {
        entity?: string;
        program?: string;
    } | null;
}

/** Which Analysis-tab accordion G/I (and header / tour Show me) should expand. */
export type AnalysisCardFocus =
    | 'auto-investigate'
    | 'analyze-graph'
    | 'follow-the-money'
    | 'holistic-trace';

/**
 * What the canvas looked like when a graph summary was produced. The summary
 * describes the whole canvas, so it goes stale as soon as nodes or edges move;
 * carrying the counts lets the panel say so instead of presenting an old
 * narrative as current.
 */
export interface GraphSummaryMeta {
    nodeCount: number;
    edgeCount: number;
    generatedAt: number;
}

export interface PanelState {
    // Floating cards
    showAnalyzeCard: boolean;
    analyzeLoading: string | null;
    analyzeResultCounts: Record<string, number>;
    clusterLoading: boolean;

    /** One-shot focus for Analysis tab accordion (I → investigate, G → analyze). */
    analysisCardFocus: AnalysisCardFocus | null;
    /** Bumped on every focus request so repeat G/I still re-expands. */
    analysisCardFocusSeq: number;

    // Modals
    showHelp: boolean;
    showLegend: boolean;
    isNoticeModalOpen: boolean;
    isCaseModalOpen: boolean;

    // Debug
    debugMode: boolean;

    // Analytics workspace (inline sections)
    analyticsSection: 'flow' | 'patterns' | 'compliance' | 'pathing';
    inlineAiSummary: string;
    inlineAiLoading: boolean;
    inlinePatterns: ScanTypology[] | null;
    inlinePatternsLoading: boolean;

    /**
     * Graph Summary inspector view. The AI summary is canvas-scoped, so it gets
     * its own panel beside the canvas rather than a card buried in the
     * node-scoped Analysis tab — the summary is what a first-time user reads to
     * understand the graph, and it should not require picking a node first.
     */
    graphSummaryOpen: boolean;
    graphSummaryMeta: GraphSummaryMeta | null;

    // Compliance
    nodeCompliance: ComplianceHit | null;
    complianceHits: ComplianceHit[];
    complianceLoading: boolean;

    // Granular actions
    actionStatuses: ActionStatuses;
    findings: Finding[];

    // â”€â”€ Actions â”€â”€
    setShowAnalyzeCard: (show: boolean) => void;
    toggleAnalyzeCard: () => void;
    setAnalyzeLoading: (loading: string | null) => void;
    setAnalyzeResultCounts: (counts: Record<string, number>) => void;
    setClusterLoading: (loading: boolean) => void;
    setAnalysisCardFocus: (focus: AnalysisCardFocus | null) => void;

    setShowHelp: (show: boolean) => void;
    setShowLegend: (show: boolean) => void;
    setNoticeModalOpen: (open: boolean) => void;
    setCaseModalOpen: (open: boolean) => void;

    setDebugMode: (mode: boolean) => void;
    toggleDebugMode: () => void;

    setAnalyticsSection: (section: PanelState['analyticsSection']) => void;
    setInlineAiSummary: (summary: string) => void;
    setInlineAiLoading: (loading: boolean) => void;
    setInlinePatterns: (patterns: ScanTypology[] | null) => void;
    setInlinePatternsLoading: (loading: boolean) => void;

    setGraphSummaryOpen: (open: boolean) => void;
    setGraphSummaryMeta: (meta: GraphSummaryMeta | null) => void;

    setNodeCompliance: (hit: ComplianceHit | null) => void;
    setComplianceHits: (hits: ComplianceHit[]) => void;
    setComplianceLoading: (loading: boolean) => void;

    setActionStatuses: (statuses: ActionStatuses) => void;
    updateActionStatus: (action: string, status: string) => void;
    setFindings: (findings: Finding[]) => void;
    addFinding: (finding: Finding) => void;
    clearFindings: () => void;

    /**
     * Reset state that should clear when selectedNode changes.
     * Called by useSelectionStore.resetOnNodeChange() for cross-store orchestration.
     */
    resetOnNodeChange: () => void;
}

export const usePanelStore = create<PanelState>((set, get) => ({
    // â”€â”€ Initial state â”€â”€
    showAnalyzeCard: false,
    analyzeLoading: null,
    analyzeResultCounts: {},
    clusterLoading: false,
    analysisCardFocus: null,
    analysisCardFocusSeq: 0,

    showHelp: false,
    showLegend: false,
    isNoticeModalOpen: false,
    isCaseModalOpen: false,

    debugMode: false,

    analyticsSection: 'flow',
    inlineAiSummary: '',
    inlineAiLoading: false,
    inlinePatterns: null,
    inlinePatternsLoading: false,

    graphSummaryOpen: false,
    graphSummaryMeta: null,

    nodeCompliance: null,
    complianceHits: [],
    complianceLoading: false,

    actionStatuses: {},
    findings: [],

    // â”€â”€ Actions â”€â”€
    setShowAnalyzeCard: (show) => set({ showAnalyzeCard: show }),
    toggleAnalyzeCard: () => set((s) => ({ showAnalyzeCard: !s.showAnalyzeCard })),
    setAnalyzeLoading: (loading) => set({ analyzeLoading: loading }),
    setAnalyzeResultCounts: (counts) => set({ analyzeResultCounts: counts }),
    setClusterLoading: (loading) => set({ clusterLoading: loading }),
    setAnalysisCardFocus: (focus) =>
        set((s) => ({
            analysisCardFocus: focus,
            analysisCardFocusSeq: focus ? s.analysisCardFocusSeq + 1 : s.analysisCardFocusSeq,
        })),
    setShowHelp: (show) => set({ showHelp: show }),
    setShowLegend: (show) => set({ showLegend: show }),
    setNoticeModalOpen: (open) => set({ isNoticeModalOpen: open }),
    setCaseModalOpen: (open) => set({ isCaseModalOpen: open }),

    setDebugMode: (mode) => set({ debugMode: mode }),
    toggleDebugMode: () => set((s) => ({ debugMode: !s.debugMode })),

    setAnalyticsSection: (section) => set({ analyticsSection: section }),
    setInlineAiSummary: (summary) => set({ inlineAiSummary: summary }),
    setInlineAiLoading: (loading) => set({ inlineAiLoading: loading }),
    setInlinePatterns: (patterns) => set({ inlinePatterns: patterns }),
    setInlinePatternsLoading: (loading) => set({ inlinePatternsLoading: loading }),

    setGraphSummaryOpen: (open) => set({ graphSummaryOpen: open }),
    setGraphSummaryMeta: (meta) => set({ graphSummaryMeta: meta }),

    setNodeCompliance: (hit) => set({ nodeCompliance: hit }),
    setComplianceHits: (hits) => set({ complianceHits: hits }),
    setComplianceLoading: (loading) => set({ complianceLoading: loading }),

    setActionStatuses: (statuses) => set({ actionStatuses: statuses }),
    updateActionStatus: (action, status) => {
        const current = get().actionStatuses;
        set({ actionStatuses: { ...current, [action]: status } });
    },
    setFindings: (findings) => set({ findings }),
    addFinding: (finding) => {
        const current = get().findings;
        set({ findings: [...current, finding] });
    },
    clearFindings: () => set({ findings: [] }),

    resetOnNodeChange: () =>
        set({
            analyticsSection: 'flow',
            // Keep graph-level Macro Analysis results across node switches —
            // AI summary / pattern scan are canvas-scoped, not node-scoped.
            nodeCompliance: null,
            complianceHits: [],
            complianceLoading: false,
            actionStatuses: {},
            findings: [],
        }),
}));

// Register cross-store reset callback (avoids circular import)
import { registerNodeChangeCallback } from './useSelectionStore';
registerNodeChangeCallback(() => usePanelStore.getState().resetOnNodeChange());
